import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import inventoryItemService from '../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import inventoryItemRecipeService, {
  extractRecipeError,
} from '../../services/inventoryItemRecipeService';
import type { SizeUnit } from '../../types/venue';
import type { InventoryItemRecipeComponent } from '../../types/execution';

/**
 * Phase 7.A.2b — inline editor for an InventoryItemRecipe ("what one serving
 * depletes"). Used inside FormatExecutionBlock when a serving option links to a
 * RECIPE_CONSUMPTION execution item.
 *
 * The operator enters inputQuantity + inputUnit per component; the backend
 * converts to canonical stock units and returns the snapshot. No conversion
 * math happens here.
 */

const SIZE_UNITS: SizeUnit[] = ['ml', 'cl', 'l', 'g', 'kg', 'unit'];

interface RecipeRow {
  inventoryItem: InventoryItemWithBalance | null;
  /** Free-text during typing; parsed on save. */
  inputQuantity: string;
  inputUnit: SizeUnit;
}

interface InventoryRecipeEditorProps {
  venueId: string;
  /** The linked RECIPE_CONSUMPTION execution item whose recipe we edit. */
  itemId: string;
  initialComponents: InventoryItemRecipeComponent[] | null;
  onSaved: () => void;
  onCancel: () => void;
}

const emptyRow = (): RecipeRow => ({ inventoryItem: null, inputQuantity: '', inputUnit: 'ml' });

/** Build a minimal item stand-in from a stored component for the picker value. */
function componentToItem(c: InventoryItemRecipeComponent): InventoryItemWithBalance {
  return {
    _id: c.inventoryItemId,
    displayName: c.displayName ?? 'Inventory item',
    sizeValue: c.componentSizeValue ?? undefined,
    sizeUnit: c.componentSizeUnit ?? undefined,
  } as unknown as InventoryItemWithBalance;
}

function seedRows(components: InventoryItemRecipeComponent[] | null): RecipeRow[] {
  if (!components || components.length === 0) return [emptyRow()];
  return components.map((c) => ({
    inventoryItem: componentToItem(c),
    // Prefer the operator input snapshot; fall back to canonical as a direct
    // stock fraction (inputUnit 'unit') for legacy/direct-authored components.
    inputQuantity: c.inputQuantity != null ? String(c.inputQuantity) : String(c.quantity),
    inputUnit: (c.inputUnit ?? 'unit') as SizeUnit,
  }));
}

const inventoryItemLabel = (item: InventoryItemWithBalance) => {
  const name = item.displayName;
  const size = item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : null;
  return size ? `${name} · ${size}` : name;
};

export default function InventoryRecipeEditor({
  venueId,
  itemId,
  initialComponents,
  onSaved,
  onCancel,
}: InventoryRecipeEditorProps) {
  const [rows, setRows] = useState<RecipeRow[]>(() => seedRows(initialComponents));
  const [searchResults, setSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const hasExistingRecipe = Boolean(initialComponents && initialComponents.length > 0);

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        if (!q.trim()) {
          setSearchResults([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        try {
          const res = await inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', q);
          setSearchResults(res.data ?? []);
        } catch {
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 350),
    [venueId],
  );
  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const updateRow = (idx: number, patch: Partial<RecipeRow>) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setError(null);
    // Validate locally before hitting the backend.
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.inventoryItem) {
        setError(`Ingredient ${i + 1}: choose an inventory item.`);
        return;
      }
      const qty = parseFloat(r.inputQuantity);
      if (!r.inputQuantity || isNaN(qty) || qty <= 0) {
        setError(`Ingredient ${i + 1}: amount must be a number greater than 0.`);
        return;
      }
    }
    const payload = {
      components: rows.map((r, i) => ({
        inventoryItemId: r.inventoryItem!._id,
        inputQuantity: parseFloat(r.inputQuantity),
        inputUnit: r.inputUnit,
        position: i,
      })),
    };
    setSaving(true);
    try {
      await inventoryItemRecipeService.putItemRecipe(venueId, itemId, payload);
      onSaved();
    } catch (err) {
      setError(extractRecipeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRecipe = async () => {
    setError(null);
    setSaving(true);
    try {
      await inventoryItemRecipeService.deleteItemRecipe(venueId, itemId);
      onSaved();
    } catch (err) {
      setError(extractRecipeError(err));
      setConfirmingRemove(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        What one serving depletes
      </Typography>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1, mb: 0.5 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={1} sx={{ mt: 1 }}>
        {rows.map((row, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 2, minWidth: 200 }}>
                <Autocomplete
                  size="small"
                  options={searchResults}
                  value={row.inventoryItem}
                  loading={searchLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={inventoryItemLabel}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') runSearch(value);
                    if (reason === 'clear') updateRow(idx, { inventoryItem: null });
                  }}
                  onChange={(_, val) => updateRow(idx, { inventoryItem: val })}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt._id}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {inventoryItemLabel(opt)}
                        </Typography>
                        {opt.balance != null && (
                          <Typography variant="caption" color="text.secondary">
                            {opt.balance.totalQuantity} in stock
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Inventory item"
                      placeholder="Search inventory…"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {searchLoading && <CircularProgress size={14} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <TextField
                size="small"
                label="Amount"
                value={row.inputQuantity}
                onChange={(e) => updateRow(idx, { inputQuantity: e.target.value })}
                type="number"
                sx={{ width: 110 }}
                slotProps={{ htmlInput: { min: 0, step: 'any' } }}
              />
              <FormControl size="small" sx={{ width: 110 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={row.inputUnit}
                  label="Unit"
                  onChange={(e) => updateRow(idx, { inputUnit: e.target.value as SizeUnit })}
                >
                  {SIZE_UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeRow(idx)}
                aria-label="Remove ingredient"
                disabled={rows.length === 1}
                sx={{ alignSelf: 'center' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        ml/g = amount per serving · unit = whole stock units
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none' }}>
          Add ingredient
        </Button>
        <Box sx={{ flex: 1 }} />
        {hasExistingRecipe &&
          (confirmingRemove ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Remove this recipe?
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={handleRemoveRecipe}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                Confirm remove
              </Button>
              <Button
                size="small"
                onClick={() => setConfirmingRemove(false)}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                Keep
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="error"
              onClick={() => setConfirmingRemove(true)}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Remove recipe
            </Button>
          ))}
        <Button size="small" onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save recipe'}
        </Button>
      </Stack>
    </Box>
  );
}
